import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { InfoClient, SubscriptionClient, fromSatoshi } from "../sdk";
import { ConnectionState } from "../sdk/types/transport";

export interface TradePriceConfig {
  baseURL: string;
  wsURL: string;
  marketId: string;
  symbol?: string; // Symbol for mark price subscription (e.g., "BTC/USDC")
  limit?: number;
  authToken?: string;
  walletAddress?: string;
  deviceID?: string;
}

export interface LastPriceData {
  market_id: string;
  price: number;
  quantity: number;
  timestamp: number;
  is_buyer_maker: boolean;
  trade_id: string;
}

export interface MarkPriceData {
  symbol: string;
  mark_price: number; // Mark price as decimal number (converted from satoshi)
  timestamp: number;
}

// Type guards for better debugging and type safety
export function isLastPriceData(value: any): value is LastPriceData {
  return (
    value !== null &&
    typeof value === "object" &&
    typeof value.price === "number" &&
    typeof value.market_id === "string" &&
    typeof value.quantity === "number" &&
    typeof value.timestamp === "number" &&
    typeof value.is_buyer_maker === "boolean" &&
    typeof value.trade_id === "string"
  );
}

export function isMarkPriceData(value: any): value is MarkPriceData {
  return (
    value !== null &&
    typeof value === "object" &&
    typeof value.symbol === "string" &&
    typeof value.mark_price === "number" &&
    typeof value.timestamp === "number"
  );
}

/**
 * Hook for managing last trade price data with HTTP initialization and WebSocket real-time updates
 * Loads the most recent 100 trades from HTTP, then subscribes to WebSocket for new updates
 * Displays the most recent trade price with side indicator (BUY/SELL)
 */
export function useTradePrice(config: TradePriceConfig) {
  const [lastPrice, setLastPrice] = useState<LastPriceData | null>(null);
  const [markPrice, setMarkPrice] = useState<MarkPriceData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [priceChange, setPriceChange] = useState<"up" | "down" | null>(null);

  const infoClientRef = useRef<InfoClient | null>(null);
  const subscriptionClientRef = useRef<SubscriptionClient | null>(null);
  const isSubscribedRef = useRef<boolean>(false);
  const isMarkPriceSubscribedRef = useRef<boolean>(false);
  const pollingIntervalRef = useRef<number | null>(null);
  const connectionTimeoutRef = useRef<number | null>(null);

  /**
   * Poll recent trades from HTTP request (for fallback when WebSocket fails)
   * Doesn't set loading state to avoid UI flicker during polling
   */
  const pollRecentTrades = useCallback(async () => {
    if (!infoClientRef.current) {
      console.warn(
        "[useTradePrice] pollRecentTrades called but infoClientRef.current is null",
      );
      return;
    }

    console.debug(
      "[useTradePrice] Polling recent trades (marketId:",
      config.marketId,
      ")",
    );
    try {
      const limit = config.limit || 100;
      const trades = await infoClientRef.current.getRecentTrades(
        config.marketId,
        limit,
      );

      if (trades && Array.isArray(trades) && trades.length > 0) {
        console.debug(
          "[useTradePrice] Polling received",
          trades.length,
          "trades for marketId:",
          config.marketId,
        );

        // Sort by timestamp descending to get the most recent trade
        const sortedTrades = [...trades].sort((a, b) => {
          const timeA = a.timestamp || 0;
          const timeB = b.timestamp || 0;
          return timeB - timeA; // Descending: newest first
        });

        const mostRecent = sortedTrades[0];
        console.debug("[useTradePrice] Most recent trade:", mostRecent);

        // Convert satoshi strings to decimal numbers
        const lastPriceData: LastPriceData = {
          market_id: mostRecent.market_id || config.marketId,
          price:
            typeof mostRecent.price === "string"
              ? fromSatoshi(mostRecent.price)
              : mostRecent.price,
          quantity:
            typeof mostRecent.quantity === "string"
              ? fromSatoshi(mostRecent.quantity)
              : mostRecent.quantity || 0,
          timestamp: mostRecent.timestamp || Date.now(),
          is_buyer_maker: mostRecent.is_buyer_maker || false,
          trade_id: mostRecent.trade_id || mostRecent.id || "",
        };

        console.debug(
          "[useTradePrice] Processed lastPriceData:",
          lastPriceData,
        );

        setLastPrice((current) => {
          // Always update to ensure timestamp and other fields are refreshed
          // Determine price change direction
          if (current && typeof current.price === "number") {
            if (lastPriceData.price > current.price) {
              setPriceChange("up");
            } else if (lastPriceData.price < current.price) {
              setPriceChange("down");
            }
          }

          // Clear price change indicator after animation
          setTimeout(() => setPriceChange(null), 500);

          // Always return new object to ensure React detects the change
          return lastPriceData;
        });
        console.debug(
          "[useTradePrice] Polling successful - received trade price:",
          lastPriceData.price,
        );
      } else {
        console.warn(
          "[useTradePrice] Polling returned no trades or invalid data:",
          trades,
        );
      }
    } catch (err) {
      // Only set error if we don't have existing data (to avoid overwriting with transient errors)
      setError(
        err instanceof Error ? err.message : "Failed to load recent trades",
      );
      console.error("[useTradePrice] Failed to poll recent trades:", err);
    }
  }, [config.marketId, config.limit]);

  /**
   * Initialize recent trades from HTTP request
   * Fetches the most recent 100 trades and sets the latest one as lastPrice
   */
  const initializeRecentTrades = useCallback(async () => {
    if (!infoClientRef.current) return;

    setLoading(true);
    setError(null);

    try {
      const limit = config.limit || 100;
      const trades = await infoClientRef.current.getRecentTrades(
        config.marketId,
        limit,
      );

      if (trades && Array.isArray(trades) && trades.length > 0) {
        // Sort by timestamp descending to get the most recent trade
        const sortedTrades = [...trades].sort((a, b) => {
          const timeA = a.timestamp || 0;
          const timeB = b.timestamp || 0;
          return timeB - timeA; // Descending: newest first
        });

        const mostRecent = sortedTrades[0];
        /**
         *
         [useTradePrice] Most recent trade:
        {
        trade_id: "trade_1765866649599060794",
        buy_order_id: "7bba9d0495eea08c9239ad050bfaf27fbcaabb2312851ca3dc66716bc89dc6eb",
        sell_order_id: "65e6c9ff85fce05cce7df08c4bc14187aa93476e8595e865dde9611e560d239d",
        price: "8198750305175",
        quantity: "1157786",
        timestamp: "1765866649",
        market_id: "0",
        maker_fee: "0",
        taker_fee: "0",
        dual_write_id: "16210999351379558260",

        }
         useTradePrice.ts:115:17

         */
        // Convert satoshi strings to decimal numbers
        const lastPriceData: LastPriceData = {
          market_id: mostRecent.market_id || config.marketId,
          price:
            typeof mostRecent.price === "string"
              ? fromSatoshi(mostRecent.price)
              : mostRecent.price,
          quantity:
            typeof mostRecent.quantity === "string"
              ? fromSatoshi(mostRecent.quantity)
              : mostRecent.quantity || 0,
          timestamp: mostRecent.timestamp || Date.now(),
          is_buyer_maker: mostRecent.is_buyer_maker || false,
          trade_id: mostRecent.trade_id || mostRecent.id || "",
        };
        setLastPrice(lastPriceData);
      } else {
        console.warn(
          "[useTradePrice] Initialization returned no trades or invalid data:",
          trades,
        );
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load recent trades",
      );
      console.error("Failed to initialize recent trades:", err);
    } finally {
      setLoading(false);
    }
  }, [config.marketId, config.limit]);

  /**
   * Handle last price update from WebSocket
   */
  const handleLastPriceUpdate = useCallback(
    (data: any) => {
      console.debug(
        "[useTradePrice] handleLastPriceUpdate called with data:",
        data,
      );

      if (!data) {
        console.warn(
          "[useTradePrice] handleLastPriceUpdate received null/undefined data",
        );
        return;
      }

      if (!data.price && data.price !== 0) {
        console.warn(
          "[useTradePrice] handleLastPriceUpdate received data without price field:",
          data,
        );
        return;
      }

      // Ensure price is a number (SubscriptionClient should convert, but be defensive)
      const price =
        typeof data.price === "string"
          ? fromSatoshi(data.price)
          : Number(data.price);
      const quantity =
        typeof data.quantity === "string"
          ? fromSatoshi(data.quantity)
          : Number(data.quantity) || 0;

      if (isNaN(price)) {
        console.warn("[useTradePrice] Invalid price value:", data.price);
        return;
      }

      setLastPrice((current) => {
        // Determine price change direction
        if (isLastPriceData(current) && typeof current.price === "number") {
          if (price > current.price) {
            setPriceChange("up");
          } else if (price < current.price) {
            setPriceChange("down");
          }
        }

        // Clear price change indicator after animation
        setTimeout(() => setPriceChange(null), 500);

        const lastPriceData: LastPriceData = {
          market_id: data.market_id || config.marketId,
          price: price,
          quantity: quantity,
          timestamp: data.timestamp || Date.now(),
          is_buyer_maker: data.is_buyer_maker || false,
          trade_id: data.trade_id || data.id || "",
        };

        // Type validation for debugging
        if (!isLastPriceData(lastPriceData)) {
          console.error(
            "[useTradePrice] Invalid LastPriceData structure:",
            lastPriceData,
          );
        }

        console.debug("[useTradePrice] Setting lastPrice to:", lastPriceData);
        console.debug(
          "[useTradePrice] Type check - isLastPriceData:",
          isLastPriceData(lastPriceData),
        );
        return lastPriceData;
      });
    },
    [config.marketId],
  );

  /**
   * Handle mark price update from WebSocket
   */
  const handleMarkPriceUpdate = useCallback(
    (data: any) => {
      if (!data) {
        console.warn(
          "[useTradePrice] handleMarkPriceUpdate received null/undefined data",
        );
        return;
      }

      if (data.mark_price === undefined || data.mark_price === null) {
        console.warn(
          "[useTradePrice] handleMarkPriceUpdate received data without mark_price field:",
          data,
        );
        return;
      }

      // Convert mark_price from satoshi string to decimal number
      const markPrice = Number(data.mark_price);

      if (isNaN(markPrice)) {
        console.warn(
          "[useTradePrice] Invalid mark_price value:",
          data.mark_price,
        );
        return;
      }

      const markPriceData: MarkPriceData = {
        symbol: data.symbol || config.symbol || "",
        mark_price: markPrice, // Converted to decimal number
        timestamp: data.timestamp || Date.now(),
      };

      // Type validation for debugging
      if (!isMarkPriceData(markPriceData)) {
        console.error(
          "[useTradePrice] Invalid MarkPriceData structure:",
          markPriceData,
        );
      }

      setMarkPrice(markPriceData);
    },
    [config.symbol],
  );

  // Initialize clients
  useEffect(() => {
    const infoClient = new InfoClient({
      baseURL: config.baseURL,
    });
    infoClientRef.current = infoClient;

    const subscriptionClient = new SubscriptionClient({
      url: config.wsURL,
      authToken: config.authToken,
      walletAddress: config.walletAddress,
      deviceID: config.deviceID,
    });
    subscriptionClientRef.current = subscriptionClient;

    // Initialize recent trades from HTTP
    initializeRecentTrades();

    // Subscribe function that can be called on initial connect or reconnection
    const subscribe = () => {
      if (subscriptionClient.isConnected()) {
        if (!isSubscribedRef.current) {
          subscriptionClient.subscribeLastPrice(
            config.marketId,
            handleLastPriceUpdate,
            true,
          );
          isSubscribedRef.current = true;
        }
        // Subscribe to mark price if symbol is provided
        if (config.symbol && !isMarkPriceSubscribedRef.current) {
          subscriptionClient.subscribeMarkPrice(
            config.symbol,
            handleMarkPriceUpdate,
          );
          isMarkPriceSubscribedRef.current = true;
        }
      }
    };

    // Connect WebSocket and subscribe
    console.debug("[useTradePrice] Attempting WebSocket connection...");
    const connectPromise = subscriptionClient.connect();
    console.debug(
      "[useTradePrice] Connect promise created, waiting for result...",
    );

    // Add a timeout fallback in case the promise never rejects
    // This ensures polling starts even if WebSocketClient doesn't properly reject
    connectionTimeoutRef.current = window.setTimeout(() => {
      console.warn(
        "[useTradePrice] WebSocket connection timeout fallback triggered after 12 seconds",
      );
      const isActuallyConnected = subscriptionClient.isConnected();
      console.warn(
        "[useTradePrice] subscriptionClient.isConnected():",
        isActuallyConnected,
        "hasPollingInterval:",
        pollingIntervalRef.current !== null,
      );
      if (
        !isActuallyConnected &&
        infoClientRef.current &&
        pollingIntervalRef.current === null
      ) {
        console.debug(
          "[useTradePrice] Starting HTTP polling due to connection timeout (marketId:",
          config.marketId,
          ")",
        );
        setIsConnected(false);
        pollRecentTrades();
        pollingIntervalRef.current = window.setInterval(() => {
          pollRecentTrades();
        }, 5000);
        console.debug(
          "[useTradePrice] HTTP polling interval set up (every 5 seconds)",
        );
      } else if (isActuallyConnected) {
        console.debug(
          "[useTradePrice] Connection established before timeout, no polling needed",
        );
      } else if (pollingIntervalRef.current !== null) {
        console.debug("[useTradePrice] Polling already started, skipping");
      }
    }, 12000); // 12 seconds (2 seconds after WebSocketClient's 10-second timeout)

    connectPromise
      .then(() => {
        if (connectionTimeoutRef.current !== null) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        console.debug("[useTradePrice] WebSocket connected successfully");
        setIsConnected(true);
        subscribe();
      })
      .catch((err) => {
        if (connectionTimeoutRef.current !== null) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        console.error(
          "[useTradePrice] WebSocket connection promise rejected:",
          err,
        );
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error";
        console.info(
          "WebSocket failed to establish connection. Falling back to HTTP polling:",
          errorMessage,
        );
        setIsConnected(false);
        // Explicitly start polling when WebSocket fails
        // Since isConnected was already false, the polling effect won't re-run
        if (infoClientRef.current) {
          // Clear any existing polling interval first
          if (pollingIntervalRef.current !== null) {
            console.debug(
              "[useTradePrice] Clearing existing polling interval (WebSocket failure handler)",
            );
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          // Start polling immediately, then every 5 seconds
          console.debug(
            "[useTradePrice] Starting HTTP polling due to WebSocket connection failure (marketId:",
            config.marketId,
            ")",
          );
          pollRecentTrades();
          pollingIntervalRef.current = window.setInterval(() => {
            pollRecentTrades();
          }, 5000);
          console.debug(
            "[useTradePrice] HTTP polling interval set up (every 5 seconds)",
          );
        } else {
          console.warn(
            "[useTradePrice] Cannot start polling: infoClientRef.current is null",
          );
        }
      });

    // Monitor connection state changes and re-subscribe on reconnection
    let unsubscribeStateChange: (() => void) | null = null;
    const wsClient = (subscriptionClient as any).wsClient;
    if (wsClient) {
      unsubscribeStateChange = wsClient.onStateChange(
        (state: ConnectionState) => {
          console.debug("[useTradePrice] WebSocket state changed to:", state);
          if (state === ConnectionState.Connected) {
            console.debug(
              "[useTradePrice] WebSocket connected - stopping polling if active",
            );
            setIsConnected(true);
            // Re-subscribe if we were previously subscribed
            if (isSubscribedRef.current) {
              subscribe();
            }
          } else if (
            state === ConnectionState.Disconnected ||
            state === ConnectionState.Reconnecting
          ) {
            console.debug(
              "[useTradePrice] WebSocket disconnected/reconnecting - polling effect should start polling",
            );
            setIsConnected(false);
            isSubscribedRef.current = false;
            isMarkPriceSubscribedRef.current = false;
          }
        },
      );
    }

    return () => {
      // Clear connection timeout
      if (connectionTimeoutRef.current !== null) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      isSubscribedRef.current = false;
      isMarkPriceSubscribedRef.current = false;

      // Unsubscribe from state changes
      if (unsubscribeStateChange) {
        unsubscribeStateChange();
      }

      if (subscriptionClientRef.current) {
        subscriptionClientRef.current.unsubscribeLastPrice(
          config.marketId,
          handleLastPriceUpdate,
        );
        if (config.symbol) {
          subscriptionClientRef.current.unsubscribeMarkPrice(
            config.symbol,
            handleMarkPriceUpdate,
          );
        }
        subscriptionClientRef.current.disconnect();
      }
    };
    // Note: We don't include callbacks in dependencies because:
    // 1. They're recreated when their dependencies change (config.marketId, config.symbol)
    // 2. They capture the latest config values via closure
    // 3. We only need to re-run this effect when actual config values change, not when callbacks are recreated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.baseURL,
    config.wsURL,
    config.marketId,
    config.symbol,
    config.authToken,
    config.walletAddress,
    config.deviceID,
  ]);

  // Re-initialize when marketId or symbol changes
  useEffect(() => {
    if (infoClientRef.current && subscriptionClientRef.current) {
      // Unsubscribe from old market
      if (subscriptionClientRef.current.isConnected()) {
        subscriptionClientRef.current.unsubscribeLastPrice(
          config.marketId,
          handleLastPriceUpdate,
        );
        isSubscribedRef.current = false;
        if (config.symbol) {
          subscriptionClientRef.current.unsubscribeMarkPrice(
            config.symbol,
            handleMarkPriceUpdate,
          );
          isMarkPriceSubscribedRef.current = false;
        }
      }

      // Initialize new market
      initializeRecentTrades();

      // Subscribe to new market
      if (subscriptionClientRef.current.isConnected()) {
        subscriptionClientRef.current.subscribeLastPrice(
          config.marketId,
          handleLastPriceUpdate,
          true,
        );
        isSubscribedRef.current = true;
        if (config.symbol) {
          subscriptionClientRef.current.subscribeMarkPrice(
            config.symbol,
            handleMarkPriceUpdate,
          );
          isMarkPriceSubscribedRef.current = true;
        }
      }
    }
    // Note: We don't include callbacks in dependencies because:
    // 1. They're recreated when config.marketId or config.symbol change
    // 2. They capture the latest config values via closure
    // 3. We only need to re-run when marketId/symbol change, not when callbacks are recreated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.marketId, config.symbol]);

  // Fallback to HTTP polling when WebSocket is disconnected
  useEffect(() => {
    console.debug(
      "[useTradePrice] Polling effect triggered - isConnected:",
      isConnected,
      "marketId:",
      config.marketId,
    );

    // Clear any existing polling interval
    if (pollingIntervalRef.current !== null) {
      console.debug(
        "[useTradePrice] Clearing existing polling interval (polling effect)",
      );
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Start polling if WebSocket is not connected
    if (!isConnected && infoClientRef.current) {
      console.debug(
        "[useTradePrice] Starting HTTP polling (WebSocket disconnected, marketId:",
        config.marketId,
        ")",
      );
      // Start polling immediately, then every 5 seconds
      pollRecentTrades();
      pollingIntervalRef.current = window.setInterval(() => {
        pollRecentTrades();
      }, 5000);
      console.debug(
        "[useTradePrice] HTTP polling interval set up (every 5 seconds)",
      );
    } else if (!isConnected && !infoClientRef.current) {
      console.warn(
        "[useTradePrice] Cannot start polling: infoClientRef.current is null (isConnected:",
        isConnected,
        ")",
      );
    } else if (isConnected) {
      console.debug("[useTradePrice] WebSocket connected, polling not needed");
    }

    return () => {
      if (pollingIntervalRef.current !== null) {
        console.debug(
          "[useTradePrice] Cleaning up polling interval (polling effect cleanup)",
        );
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
    // Note: pollRecentTrades is a useCallback that depends on config.marketId and config.limit
    // We don't need to include it in dependencies - it will use the latest values via closure
    // We only need to re-run when isConnected or marketId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, config.marketId]);

  // Compute display price and timestamp: use whichever has the later timestamp (lastPrice or markPrice)
  // Note: markPrice subscription ensures markPrice is always available when symbol is provided
  const { displayPrice, displayTimestamp } = useMemo(() => {
    const hasLastPrice = isLastPriceData(lastPrice) && lastPrice.price != null;
    const hasMarkPrice =
      isMarkPriceData(markPrice) && markPrice.mark_price != null;

    // If both are available, compare timestamps and use the more recent one
    if (hasLastPrice && hasMarkPrice) {
      const lastPriceTimestamp = lastPrice.timestamp || 0;
      const markPriceTimestamp = markPrice.timestamp || 0;

      if (lastPriceTimestamp >= markPriceTimestamp) {
        // lastPrice is more recent or equal

        return {
          displayPrice: lastPrice.price,
          displayTimestamp: lastPriceTimestamp,
        };
      } else {
        return {
          displayPrice: markPrice.mark_price, // Already a number, no parsing needed
          displayTimestamp: markPriceTimestamp,
        };
      }
    }

    // If only lastPrice is available (markPrice not yet received or symbol not provided)
    if (hasLastPrice) {
      return {
        displayPrice: lastPrice.price,
        displayTimestamp: lastPrice.timestamp || 0,
      };
    }

    // If only markPrice is available (lastPrice not yet received)
    // markPrice subscription ensures this is always available when symbol is provided
    if (hasMarkPrice) {
      return {
        displayPrice: markPrice.mark_price, // Already a number, no parsing needed
        displayTimestamp: markPrice.timestamp || 0,
      };
    }

    // This should only occur during initial loading before markPrice subscription establishes
    // Once markPrice subscription is active (when symbol is provided), markPrice will always be available
    // If symbol is not provided, lastPrice should be available from last_price subscription
    return {
      displayPrice: null,
      displayTimestamp: null,
    };
  }, [lastPrice, markPrice]); // Use whole objects to ensure updates are detected

  return {
    lastPrice,
    markPrice,
    displayPrice,
    displayTimestamp,
    loading,
    error,
    isConnected,
    priceChange,
    refresh: initializeRecentTrades,
    // Export type guards for external debugging
    isLastPriceData,
    isMarkPriceData,
  };
}
